import { useI18n } from "vue-i18n";

/**
 * Common translateLabel utility function (Composable version)
 * Provides consistent translation logic across all components with dynamic key resolution
 * 
 * This function attempts to translate labels using a hierarchical fallback strategy:
 * 1. Direct translation: Checks for exact label match (e.g., 'user.name')
 * 2. Nested name: Checks for label + '.name' suffix (e.g., 'user' → 'user.name')
 * 3. Display title: Checks for label + '.displayTitle' suffix (e.g., 'user' → 'user.displayTitle')
 * 4. Fallback: Returns original label if no translation found
 * 
 * @param {string} label - The label/key to translate. Can be a simple key or dot-notation path
 * @returns {string} - The translated label or original label if no translation found
 * 
 * @example
 * // Direct translation
 * translateLabel('commonLabels.save') // Returns translated 'Save'
 * 
 * @example
 * // Nested name resolution
 * translateLabel('user') // Tries 'user.name', then returns 'user' if not found
 * 
 * @example
 * // Display title resolution
 * translateLabel('template') // Tries 'template.displayTitle', then 'template.name', then 'template'
 */
export function useTranslateLabel() {
  const { t, te } = useI18n();

  const translateLabel = (label) => {
    if (!label) return '';
    switch (true) {
      case te(label):
        return t(label);
      case te(`${label}.name`):
        return t(`${label}.name`);
      case te(`${label}.displayTitle`):
        return t(`${label}.displayTitle`);
      default:
        return label;
    }
  };

  return { translateLabel };
}

/**
 * Simple translateLabel function for non-composable usage
 * Provides consistent translation logic with dynamic key resolution (same logic as composable version)
 * 
 * This function attempts to translate labels using a hierarchical fallback strategy:
 * 1. Direct translation: Checks for exact label match (e.g., 'user.name')
 * 2. Nested name: Checks for label + '.name' suffix (e.g., 'user' → 'user.name')
 * 3. Display title: Checks for label + '.displayTitle' suffix (e.g., 'user' → 'user.displayTitle')
 * 4. Fallback: Returns original label if no translation found
 * 
 * @param {string} label - The label/key to translate. Can be a simple key or dot-notation path
 * @param {object} i18n - The i18n instance with t and te functions (from useI18n())
 * @returns {string} - The translated label or original label if no translation found
 * 
 * @example
 * const { t, te } = useI18n();
 * translateLabel('estateSectionLabels.wills', { t, te }); // Returns translated section name
 * 
 * @example
 * // With dynamic key resolution
 * translateLabel('beneficiary', { t, te }); // Tries 'beneficiary.name', then 'beneficiary.displayTitle', then 'beneficiary'
 */
export function translateLabel(label, i18n) {
  if (!label) return '';
  if (!i18n) return label;
  
  const { t, te } = i18n;
  switch (true) {
    case te(label):
      return t(label);
    case te(`${label}.name`):
      return t(`${label}.name`);
    case te(`${label}.displayTitle`):
      return t(`${label}.displayTitle`);
    default:
      return label;
  }
}
